<template>
  <div class="affiliate-page">
    <div class="affiliate-shell">
      <header class="affiliate-topbar" aria-label="Affiliate dashboard">
        <div class="affiliate-brand" aria-label="Taption">
          <div class="affiliate-mark" aria-hidden="true"></div>
          <div class="affiliate-brand__text">
            <div class="affiliate-brand__name">Taption</div>
            <div class="affiliate-brand__page">{{ msg }}</div>
          </div>
        </div>
        <div class="affiliate-user-section">
          <span class="welcome-text">Welcome {{ loginEmail }}</span>
          <v-btn
            @click="handleLogout"
            text
            small
            color="primary"
            class="logout-btn"
          >
            Logout
          </v-btn>
        </div>
      </header>

      <section class="affiliate-controls" :class="{ 'is-disabled': isLoadingChart }">
        <v-row class="selector-row" dense>
           <v-col cols="12" sm="6" :md="isAdmin ? 3 : 4">
               <label class="selector-label">Month to Date:</label>
             <v-select
               :items="dates"
               :disabled="isLoading || isLoadingChart"
               :loading="isLoading"
               v-model="selectedDate"
               item-text="text"
               @change="onDateChange"
               outlined
               return-object
               dense
               hide-details
             >
             </v-select>
           </v-col>
         <v-col v-if="isAdmin" cols="12" sm="6" md="3">
           <label class="selector-label">Account:</label>
           <v-select
             :items="emailOptions"
             :disabled="isLoading || isLoadingChart || !selectedDate.value"
             :loading="isLoadingChart"
             v-model="selectedEmail"
             @change="onEmailChange"
             outlined
             dense
             hide-details
             clearable
           >
           </v-select>
         </v-col>
         <v-col cols="12" sm="6" :md="isAdmin ? 3 : 4">
           <label class="selector-label">Platform:</label>
           <v-select
             :items="platformOptions"
             :disabled="isLoading || isLoadingChart || !selectedDate.value || platformOptions.length === 0"
             v-model="selectedPlatforms"
             @change="onPlatformsChange"
             multiple
             chips
             deletable-chips
             :menu-props="{ closeOnContentClick: false }"
             outlined
             dense
             hide-details
             clearable
             >
             <template v-slot:item="{ item, on, attrs }">
               <v-list-item v-on="on" v-bind="attrs">
                 <v-list-item-action>
                   <v-checkbox :input-value="selectedPlatforms.includes(item)" @click.stop="togglePlatformSelection(item)" />
                 </v-list-item-action>
                 <v-list-item-content>
                   <v-list-item-title>{{ item }}</v-list-item-title>
                 </v-list-item-content>
               </v-list-item>
             </template>
             </v-select>
           </v-col>
         <v-col cols="12" sm="6" :md="isAdmin ? 3 : 4">
           <label class="selector-label">Category:</label>
           <v-select
             :items="categoryOptions"
             :disabled="isLoading || isLoadingChart || !selectedDate.value || categoryOptions.length === 0"
             v-model="selectedCategories"
             @change="onCategoriesChange"
             item-text="text"
             item-value="value"
             multiple
             chips
             deletable-chips
             :menu-props="{ closeOnContentClick: false }"
             outlined
             dense
             hide-details
             clearable
           >
             <template v-slot:item="{ item, on, attrs }">
               <v-list-item v-on="on" v-bind="attrs" :disabled="isCategoryOptionDisabled(item.value)">
                 <v-list-item-action>
                   <v-checkbox
                     :input-value="selectedCategories.includes(item.value)"
                     :disabled="isCategoryOptionDisabled(item.value)"
                     @click.stop="toggleCategorySelection(item.value)"
                   />
                 </v-list-item-action>
                 <v-list-item-content>
                   <v-list-item-title>{{ item.text }}</v-list-item-title>
                 </v-list-item-content>
               </v-list-item>
             </template>
           </v-select>
         </v-col>
        </v-row>
      </section>

      <section class="affiliate-charts">
        <div v-if="isLoadingChart" class="chart-state-card">
          <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
        </div>

        <div v-else-if="!selectedEmail || chartConfigs.length === 0" class="chart-state-card chart-state-card--placeholder">
          <p class="placeholder-text">{{ chartPlaceholderText }}</p>
        </div>

        <v-fade-transition>
          <div v-if="!isLoadingChart && selectedEmail && chartConfigs.length > 0">
            <v-row dense class="chart-grid">
              <v-col
                v-for="(cfg, index) in chartConfigs"
                :key="cfg.key"
                cols="12"
                :sm="12"
                :md="getChartMd(chartConfigs.length, index)"
              >
                <div class="chart-card">
                  <div class="chart-card-header">
                    <div class="chart-card-title">{{ cfg.title }}</div>
                    <v-btn
                      icon
                      x-small
                      class="chart-card-close"
                      :aria-label="`Remove ${cfg.title}`"
                      @click.stop="removeCategory(cfg.categoryKey)"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div v-if="cfg.series.length === 0" class="chart-empty">
                    <p class="placeholder-text">No data available</p>
                  </div>
                  <apexchart
                    v-else
                    type="line"
                    height="320"
                    :options="cfg.options"
                    :series="cfg.series"
                  ></apexchart>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-fade-transition>
      </section>

      <section class="affiliate-table-section">
        <div class="table-card" :class="{ 'is-disabled': isLoadingChart }">
          <div class="table-card-header">
            <div class="table-card-title">Details</div>
          </div>
          <v-fade-transition>
            <v-data-table
              v-if="!isLoading"
              :headers="visibleHeaders"
              :items="filteredResults"
              :items-per-page="100"
              :loading="isLoading || isLoadingChart"
              :options.sync="options"
              @update:options="updateSort"
              class="affiliate-table"
            >
              <template v-slot:item.monthlyTotal="{ item }">{{ formatUsd(item.monthlyTotal) }}</template>
              <template v-slot:item.totalCommission="{ item }">{{ formatUsd(item.totalCommission) }}</template>
              <template v-slot:item.creditAmount="{ item }">{{ formatCents(item.creditAmount) }}</template>
              <template v-slot:item.premiumCycleAmount="{ item }">{{ formatCents(item.premiumCycleAmount) }}</template>
              <template v-slot:item.corpCycleAmount="{ item }">{{ formatCents(item.corpCycleAmount) }}</template>
              <template v-slot:item.totalAmount="{ item }">{{ formatCents(item.totalAmount) }}</template>
            </v-data-table>
          </v-fade-transition>
          <div v-if="isLoading" class="table-loading">
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
          </div>
        </div>
      </section>


    <!-- <div v-if="account.type === 'admin'" v-for="(account, index) in results" :key="index">
      <h1 v-if="account.email">{{account.email}}  -  {{account.platform}}</h1>
      <h1 v-if="!account.email">{{account.platform}}</h1>
      <h4>Registered: {{account.registered}}</h4>
      <h4>Basic Credits: {{account.basicCredit}}<span v-if="account.type === 'admin'">, Basic Total:{{account.basicCredit*8}}</span></h4>
      <h4>Premium Subscribers: {{account.premium}}<span v-if="account.type === 'admin'">, Premium Credits: {{account.premiumCredit}}, Premium Total:{{account.premium*12+account.premiumCredit*6}}</span></h4>
      <h4>Corp Subscribers: {{account.corp}}<span v-if="account.type === 'admin'">, Corp Credits: {{account.corpCredit}}, Premium Total:{{account.corp*69+account.corpCredit*3}}</span></h4>
      <h4 v-if="account.type === 'admin'">Monthly Total: {{account.basicCredit*8 + account.premium*12+account.premiumCredit*6 + account.corp*69+account.corpCredit*3}}</h4>
    </div> -->
    </div>
  </div>
</template>

<script>

import systemConnection from '@/connections/system.service'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: "affform",
  components: {
    apexchart: VueApexCharts,
  },
  data: function () {
      return {
      headers: [
          { text: 'Platforms(s)', value: 'platform' },
          {
            text: 'New Register(s)',
            value: 'registered',
          },
          { text: '60 Minutes', value: 'basicCredit' },
          { text: 'Premium Subscriber(s)', value: 'premium' },
          { text: 'Bulk Subscriber(s)', value: 'corp' }
        ],
      msg2: "Select your month",
      results:[],
        dates:[{text:'Please select...',value:'',mode:'month'}],
        selectedDate:{text:'Please select...',value:'',mode:'month'},
      isLoading: false,
      isAdmin: false,
      isMember: false,
      isAffiliate: false,
      loginEmail: null,
      options: {
        sortBy: [],
        sortDesc: [], // This controls the sort direction
      },
        // Chart related data
        emailOptions: [],
        selectedEmail: null,
        platformOptions: [],
        selectedPlatforms: [],
        categoryOptions: [],
        selectedCategories: [],
        maxSelectedCategories: 6,
        isLoadingChart: false,
        chartData: [],
        seriesColors: {
          'New Registers': '#1E88E5',
          '60 Minutes': '#43A047',
          'Premium Subscribers': '#FB8C00',
          'Bulk Subscribers': '#8E24AA'
        },
        baseChartOptions: {
          chart: {
            id: 'affiliate-chart',
            toolbar: {
              show: false
            },
            zoom: {
              enabled: false
            },
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
            foreColor: 'rgba(15, 23, 42, 0.62)',
            animations: {
              enabled: true,
              animateGradually: {
                enabled: false
              },
              dynamicAnimation: {
                enabled: true,
                speed: 350
              }
            },
            events: {}
          },
          grid: {
            borderColor: 'rgba(15, 23, 42, 0.10)',
            strokeDashArray: 3,
            padding: {
              left: 8,
              right: 8
            }
          },
          dataLabels: {
            enabled: false
          },
          xaxis: {
            categories: [],
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
              style: {
                colors: 'rgba(15, 23, 42, 0.55)'
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                colors: 'rgba(15, 23, 42, 0.55)'
              }
            }
          },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        markers: {
          size: 0,
          hover: {
            size: 5
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          shared: true,
          intersect: false
        }
      }
    };
  },
  methods: {
    getChartMd(total, index) {
      const count = Number(total) || 0;
      if (count <= 1) return 12;
      if (count === 2) return 6;
      if (count === 4) return 6;
      if (count === 5) return index < 3 ? 4 : 6;
      return 4;
    },
    formatUsd(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '$0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(num);
      },
    // "*Amount" fields are stored in cents
    isCentsField(field) {
      return /Amount$/.test(String(field));
    },
    formatDollars(value) {
      const num = Number(value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number.isFinite(num) ? num : 0);
    },
    formatCents(value) {
      return this.formatDollars((Number(value) || 0) / 100);
    },
      getMonthDays(){
        const me = this;
        const yearmonth = me.selectedDate && me.selectedDate.value ? String(me.selectedDate.value) : '';
        if (!/^\d{6}$/.test(yearmonth)) return [];
        const year = Number(yearmonth.slice(0, 4));
        const month = Number(yearmonth.slice(4, 6)); // 1-12
        const daysInMonth = new Date(year, month, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, i) => i + 1);
      },
    onDateChange(dataObject){
      const me = this
      me.reset();
        const authUser = systemConnection.getAuthUser && systemConnection.getAuthUser();
        me.loginEmail = (authUser && authUser.email) ? authUser.email : me.loginEmail;

        if (!dataObject || !dataObject.value) return;

        if (dataObject.mode === 'ytd') {
          me.isLoading = false;
          me.isLoadingChart = true;
          const now = new Date();
          const probeMonth = ("0" + (now.getMonth() + 1)).slice(-2);
          const probeYearMonth = `${now.getFullYear()}${probeMonth}`;

        systemConnection.getDetailsByMonth(probeYearMonth).then(function(results){
            me.isAdmin = !!(results.data && results.data[0] && results.data[0].type === 'admin');
            me.isMember = !!(results.data && results.data[0] && results.data[0].type === 'member');
            me.isAffiliate = !!(results.data && results.data[0] && results.data[0].type === 'affiliate');
              me.headers = me.isMember ? [
              { text: 'Platforms(s)', value: 'platform' },
              { text: 'New Register(s)', value: 'registered' },
              { text: '60 Minutes', value: 'basicCredit' },
              { text: 'Premium Subscriber(s)', value: 'premium' },
              { text: 'Bulk Subscriber(s)', value: 'corp' },
              { text: 'Credit Amount($)', value: 'creditAmount' },
              { text: 'Premium Cycle Amount($)', value: 'premiumCycleAmount' },
              { text: 'Bulk Cycle Amount($)', value: 'corpCycleAmount' },
              { text: 'Total Amount($)', value: 'totalAmount' },
              { text: 'Total Commission(USD)', value: 'totalCommission' },
            ] : [
              { text: 'Platforms(s)', value: 'platform' },
              { text: 'New Register(s)', value: 'registered' },
              { text: '60 Minutes', value: 'basicCredit' },
              { text: 'Premium Subscriber(s)', value: 'premium' },
              { text: 'Bulk Subscriber(s)', value: 'corp' },
              { text: 'Credit Amount($)', value: 'creditAmount' },
              { text: 'Premium Cycle Amount($)', value: 'premiumCycleAmount' },
              { text: 'Bulk Cycle Amount($)', value: 'corpCycleAmount' },
              { text: 'Total Amount($)', value: 'totalAmount' },
                { text: 'Total(USD)', value: 'monthlyTotal' }
              ];
              me.updateCategoryOptionsFromHeaders();

              if(me.isAdmin){
                const uniqueEmails = [...new Set((results.data || []).map(d => d.email))];
                me.emailOptions = uniqueEmails.filter(e => e);
              me.results = [];
              me.isLoadingChart = false;
            }
            else{
              me.emailOptions = [];
              if (me.loginEmail) {
                me.selectedEmail = me.loginEmail;
                me.onEmailChange(me.selectedEmail);
              } else {
                me.isLoadingChart = false;
              }
            }
          }).catch(function(error){
            console.error('Error loading YTD permissions:', error);
            me.isLoadingChart = false;
          });
          return;
        }

        me.isLoading = true;
        systemConnection.getDetailsByMonth(dataObject.value).then(function(results){
          me.isAdmin = !!(results.data && results.data[0] && results.data[0].type === 'admin');
          me.isMember = !!(results.data && results.data[0] && results.data[0].type === 'member');
          me.isAffiliate = !!(results.data && results.data[0] && results.data[0].type === 'affiliate');

          if(me.isAdmin){

          // Extract unique emails for the dropdown
          const uniqueEmails = [...new Set(results.data.map(d => d.email))];
          me.emailOptions = uniqueEmails.filter(e => e);
        }
        else{
          me.emailOptions = [];
          if (me.loginEmail) {
            me.selectedEmail = me.loginEmail;
          } else {
            const uniqueEmails = [...new Set((results.data || []).map(d => d.email).filter(e => e))];
            if (uniqueEmails.length === 1) me.selectedEmail = uniqueEmails[0];
          }
        }
          me.setHeaders(results);
          me.results = results.data;
          me.updatePlatformOptionsFromResults();
          me.isLoading = false;

          if (!me.isAdmin && me.selectedEmail) {
            me.onEmailChange(me.selectedEmail);
          }
        })
      },
      setHeaders(){
        const me = this;
        if(me.isAdmin){
          //   results.data = results.data.map(function(d){
          //     d.monthlyTotal = d.basicCredit*8 + d.premium*12+d.premiumCredit*6 + d.corp*69 + d.corpCredit*3;
          //   d.basicTotal = d.basicCredit*8;
          //   d.premiumTotal = d.premium*12+d.premiumCredit*6;
          //   d.bulkTotal = d.corp*69+d.corpCredit*4;
          //   return d;
          // });

          me.headers = [
          { text: 'Email', value: 'email' },
          { text: 'Platforms(s)', value: 'platform' },
          {
            text: 'New Register(s)',
            value: 'registered',
          },
          { text: '60 Minutes', value: 'basicCredit' },
          { text: 'Premium Subscriber(s)', value: 'premium' },
          { text: 'Premium Credit(s)', value: 'premiumCredit' },
          { text: 'Bulk Subscriber(s)', value: 'corp' },
          { text: 'Bulk Credit(s)', value: 'corpCredit' },
          { text: '60 Minutes Total', value: 'basicTotal' },
          { text: 'Premium Total', value: 'premiumTotal' },
          { text: 'Bulk Total', value: 'bulkTotal' },
          { text: 'Credit Amount($)', value: 'creditAmount' },
          { text: 'Premium Cycle Amount($)', value: 'premiumCycleAmount' },
          { text: 'Bulk Cycle Amount($)', value: 'corpCycleAmount' },
          { text: 'Total Amount($)', value: 'totalAmount' },
          { text: 'Total(USD)', value: 'monthlyTotal' }
        ];

          // // Extract unique emails for the dropdown
          // const uniqueEmails = [...new Set(results.data.map(d => d.email))];
          // me.emailOptions = uniqueEmails.filter(e => e);
        }
        else{
          // results.data = (results.data || []).map(function(d){
          //   d.monthlyTotal = d.basicCredit*8 + d.premium*12 + d.corp*69;
          //   if (me.isMember && (d.totalCommission === undefined || d.totalCommission === null)) {
          //     d.totalCommission = d.basicCredit * 1 + d.premium * 2 + d.corp * 10;
          //   }
          //   return d;
          // });

            me.headers = [
              { text: 'Platforms(s)', value: 'platform' },
              {
                text: 'New Register(s)',
                value: 'registered',
              },
              { text: '60 Minutes', value: 'basicCredit' },
              { text: 'Premium Subscriber(s)', value: 'premium' },
              { text: 'Bulk Subscriber(s)', value: 'corp' },
              { text: 'Credit Amount($)', value: 'creditAmount' },
              { text: 'Premium Cycle Amount($)', value: 'premiumCycleAmount' },
              { text: 'Bulk Cycle Amount($)', value: 'corpCycleAmount' },
              { text: 'Total Amount($)', value: 'totalAmount' },
              ...(me.isMember ? [{ text: 'Total Commission(USD)', value: 'totalCommission' }] : []),
              ...(me.isMember ? [] : [{ text: 'Total(USD)', value: 'monthlyTotal' }])
            ];
          // me.emailOptions = [];
          // if (me.loginEmail) {
          //   me.selectedEmail = me.loginEmail;
          // } else {
          //   const uniqueEmails = [...new Set((results.data || []).map(d => d.email).filter(e => e))];
          //   if (uniqueEmails.length === 1) me.selectedEmail = uniqueEmails[0];
          // }
        }
        me.updateCategoryOptionsFromHeaders();
      },
      updateCategoryOptionsFromHeaders(){
        const me = this;
        const rawHeaders = me.visibleHeaders;
        const options = rawHeaders
          .filter(h => h && h.value && h.value !== 'platform' && h.value !== 'email')
          .map(h => ({ text: h.text, value: h.value }));

        me.categoryOptions = options;
        const allowed = new Set(options.map(o => o.value));
        me.selectedCategories = (me.selectedCategories || []).filter(v => allowed.has(v));

        if (!me.selectedCategories || me.selectedCategories.length === 0) {
          const preferred = me.isMember ? 'totalCommission' : (me.isAffiliate ? 'totalAmount' : 'monthlyTotal');
          if (allowed.has(preferred)) {
            me.selectedCategories = [preferred];
          } else if (options.length > 0) {
            me.selectedCategories = [options[0].value];
          }
        }

        if (me.selectedCategories.length > me.maxSelectedCategories) {
          me.selectedCategories = me.selectedCategories.slice(0, me.maxSelectedCategories);
        }
      },
      updatePlatformOptionsFromResults(){
        const me = this;
        const items = me.results || [];
        const hasUsableEmail = items.some(item => item && item.email);
        const platformSource = (me.selectedEmail && hasUsableEmail)
          ? items.filter(item => item && item.email === me.selectedEmail)
          : items;
        const uniquePlatforms = [...new Set(platformSource.map(d => d && d.platform).filter(Boolean))];
        me.platformOptions = uniquePlatforms;
        me.selectedPlatforms = (me.selectedPlatforms || []).filter(p => me.platformOptions.includes(p));
      },
      reset(){
        const me = this
        me.results = [];
        me.isLoading = false;
        me.options = { ...me.options, sortBy: [], sortDesc: [] };
        me.isMember = false;
        me.isAffiliate = false;
        me.chartData = [];
        me.selectedEmail = null;
        me.selectedPlatforms = [];
        me.platformOptions = [];
        me.categoryOptions = [];
        me.selectedCategories = [];
      },
    updateSort(newOptions) {
      if (newOptions.sortBy.length > 0) {
        const index = this.options.sortBy.indexOf(newOptions.sortBy[0]);
        if (index === -1) {
          // If not already sorted, sort descending first
          newOptions.sortDesc = [true];
        } else {
          // Toggle sort order
          newOptions.sortDesc[index] = !this.options.sortDesc[index];
        }
      }
      this.options = newOptions;
    },
        onEmailChange(email){
          const me = this;
          if(!email || !me.selectedDate.value) return;

          me.isLoadingChart = true;
          me.setHeaders();
          me.updatePlatformOptionsFromResults();
          me.updateCategoryOptionsFromHeaders();
          if (me.selectedDate && me.selectedDate.mode === 'ytd') {
            me.generateYearToDateChart();
            return;
          }
            
            systemConnection.getDailyDetailsByMonth(me.selectedDate.value, email).then(function(results){
              me.chartData = results.data || [];
              me.isLoadingChart = false;
          }).catch(function(error){
            console.error('Error loading chart data:', error);
            me.isLoadingChart = false;
          });
        },
        onPlatformsChange(platforms){
          const me = this;
          me.selectedPlatforms = Array.isArray(platforms) ? platforms : [];
        },
        onCategoriesChange(categories){
          const me = this;
          const next = Array.isArray(categories) ? categories : [];
          const allowed = new Set((me.categoryOptions || []).map(o => o.value));
          const filtered = next.filter(v => allowed.has(v));
          me.selectedCategories = filtered.slice(0, me.maxSelectedCategories);
        },
        isCategoryOptionDisabled(categoryValue){
          const me = this;
          const selected = Array.isArray(me.selectedCategories) ? me.selectedCategories : [];
          if (selected.includes(categoryValue)) return false;
          return selected.length >= me.maxSelectedCategories;
        },
      toggleCategorySelection(categoryValue){
        const me = this;
        const current = Array.isArray(me.selectedCategories) ? me.selectedCategories : [];
        if (current.includes(categoryValue)) {
          me.onCategoriesChange(current.filter(v => v !== categoryValue));
          return;
        }
        if (current.length >= me.maxSelectedCategories) return;
        me.onCategoriesChange([...current, categoryValue]);
      },
      removeCategory(categoryValue){
        const me = this;
        const current = Array.isArray(me.selectedCategories) ? me.selectedCategories : [];
        me.onCategoriesChange(current.filter(v => v !== categoryValue));
      },
      togglePlatformSelection(platform){
        const me = this;
        const current = Array.isArray(me.selectedPlatforms) ? me.selectedPlatforms : [];
        const next = current.includes(platform) ? current.filter(p => p !== platform) : [...current, platform];
        me.onPlatformsChange(next);
      },
        getSeriesColor(seriesName){
          const me = this;
          if (me.seriesColors[seriesName]) return me.seriesColors[seriesName];

        const palette = [
          '#1E88E5', '#D81B60', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1',
          '#F4511E', '#3949AB', '#7CB342', '#5E35B1', '#00897B', '#C0CA33'
        ];
        const used = new Set(Object.values(me.seriesColors));
        let hash = 0;
        for (let i = 0; i < seriesName.length; i++) {
          hash = (hash * 31 + seriesName.charCodeAt(i)) >>> 0;
        }
        const startIndex = hash % palette.length;
        for (let offset = 0; offset < palette.length; offset++) {
          const color = palette[(startIndex + offset) % palette.length];
          if (!used.has(color)) {
            me.seriesColors[seriesName] = color;
            return color;
          }
        }
          me.seriesColors[seriesName] = palette[startIndex];
          return me.seriesColors[seriesName];
        },
      async generateYearToDateChart(){
        const me = this;
        try {
          const year = (me.selectedDate && me.selectedDate.year) ? Number(me.selectedDate.year) : new Date().getFullYear();
          const email = me.isAdmin ? me.selectedEmail : me.loginEmail;

          await me.$store.dispatch('userPerformance/loadYearToDateMonths', { year, email });

          const ytd = me.$store.state.userPerformance.ytd;
          const rows = (ytd && ytd.rows) ? ytd.rows : [];

          me.chartData = rows;

        const tableByPlatform = {};
        rows.forEach(r => {
          if (!r || !r.platform) return;
          if (!tableByPlatform[r.platform]) {
            tableByPlatform[r.platform] = {
              platform: r.platform,
              email: r.email || null,
              registered: 0,
              basicCredit: 0,
              premiumCredit: 0,
              premium: 0,
              corpCredit: 0,
              corp: 0,
              premiumCycleAmount: 0,
              corpCycleAmount: 0,
              creditAmount: 0,
              totalAmount: 0,
              totalCommission: 0,
              monthlyTotal: 0,
              basicTotal: 0,
              premiumTotal: 0,
              bulkTotal: 0
            };
          }
          tableByPlatform[r.platform].registered += Number(r.registered) || 0;
          tableByPlatform[r.platform].basicCredit += Number(r.basicCredit) || 0;
          tableByPlatform[r.platform].premiumCredit += Number(r.premiumCredit) || 0;
          tableByPlatform[r.platform].premium += Number(r.premium) || 0;
          tableByPlatform[r.platform].corpCredit += Number(r.corpCredit) || 0;
          tableByPlatform[r.platform].corp += Number(r.corp) || 0;
          tableByPlatform[r.platform].premiumCycleAmount += Number(r.premiumCycleAmount) || 0;
          tableByPlatform[r.platform].corpCycleAmount += Number(r.corpCycleAmount) || 0;
          tableByPlatform[r.platform].creditAmount += Number(r.creditAmount) || 0;
          tableByPlatform[r.platform].totalAmount += Number(r.totalAmount) || 0;
          tableByPlatform[r.platform].totalCommission += Number(r.totalCommission) || 0;
          tableByPlatform[r.platform].monthlyTotal += Number(r.monthlyTotal) || 0;
          tableByPlatform[r.platform].basicTotal += Number(r.basicTotal) || 0;
          tableByPlatform[r.platform].premiumTotal += Number(r.premiumTotal) || 0;
          tableByPlatform[r.platform].bulkTotal += Number(r.bulkTotal) || 0;
        });
        me.results = Object.values(tableByPlatform);
          me.updatePlatformOptionsFromResults();
          me.updateCategoryOptionsFromHeaders();
          } catch (error) {
            console.error('Error loading YTD chart data:', error);
          } finally {
            me.isLoadingChart = false;
          }
        },
      async handleLogout() {
        const me = this;
        try {
          await systemConnection.signOutUser();
          me.$router.push('/');
        } catch (error) {
          console.error('Error during logout:', error);
        }
      },
      },
    computed: {
      msg() {
        return 'Dashboard';
      },
      chartPlaceholderText() {
        return 'No data available';
      },
      chartConfigs(){
        const me = this;
        if (!me.selectedDate || !me.selectedDate.value) return [];
        if (!me.selectedEmail) return [];

        const selectedCategories = Array.isArray(me.selectedCategories) ? me.selectedCategories : [];
        if (selectedCategories.length === 0) return [];

        const categoryLookup = new Map((me.categoryOptions || []).map(o => [o.value, o.text]));
        const mode = me.selectedDate.mode;

        const xInfo = (() => {
          if (mode === 'ytd') {
            const ytd = me.$store && me.$store.state && me.$store.state.userPerformance ? me.$store.state.userPerformance.ytd : null;
            const months = (ytd && Array.isArray(ytd.months)) ? ytd.months : [];
            return {
              keys: months.map(m => String(m.yearmonth)),
              labels: months.map(m => String(m.label)),
              title: 'Months'
            };
          }
          const days = me.getMonthDays();
          return {
            keys: days,
            labels: days.map(d => String(d)),
            title: 'Days'
          };
        })();

        const buildSeries = (categoryKey) => {
          const data = Array.isArray(me.chartData) ? me.chartData : [];
          const platformData = {};

          if (mode === 'ytd') {
            data.forEach(item => {
              if (!item || !item.platform || !item.yearmonth) return;
              const platform = item.platform;
              const key = String(item.yearmonth);
              if (!platformData[platform]) platformData[platform] = {};
              const value = Number(item[categoryKey]);
              platformData[platform][key] = (platformData[platform][key] || 0) + (Number.isFinite(value) ? value : 0);
            });
          } else {
            data.forEach(item => {
              if (!item || !item.platform || !item.date) return;
              const platform = item.platform;
              const dayStr = String(item.date).slice(-2);
              const day = parseInt(dayStr, 10);
              if (!Number.isFinite(day)) return;
              if (!platformData[platform]) platformData[platform] = {};
              const value = Number(item[categoryKey]);
              platformData[platform][day] = (platformData[platform][day] || 0) + (Number.isFinite(value) ? value : 0);
            });
          }

          let platforms = Object.keys(platformData);
          if (me.selectedPlatforms && me.selectedPlatforms.length > 0) {
            platforms = platforms.filter(p => me.selectedPlatforms.includes(p));
          }

          const isCents = me.isCentsField(categoryKey);
          return platforms.map(platform => ({
            name: platform,
            data: xInfo.keys.map(k => {
              const value = platformData[platform][k] || 0;
              return isCents ? value / 100 : value;
            })
          }));
        };

        return selectedCategories
          .slice(0, me.maxSelectedCategories)
          .map(categoryKey => {
            const title = categoryLookup.get(categoryKey) || String(categoryKey);
            const series = buildSeries(categoryKey);
            const dollarFormat = me.isCentsField(categoryKey) ? { formatter: v => me.formatDollars(v) } : {};
            const colors = series.map(s => me.getSeriesColor(s.name));
          return {
            key: `${mode}-${categoryKey}`,
            categoryKey,
            title,
            series,
            options: {
              ...me.baseChartOptions,
                colors,
                xaxis: {
                  ...me.baseChartOptions.xaxis,
                  categories: xInfo.labels,
                  tickAmount: Math.min(8, xInfo.labels.length),
                  labels: {
                    ...((me.baseChartOptions.xaxis && me.baseChartOptions.xaxis.labels) ? me.baseChartOptions.xaxis.labels : {}),
                    show: true,
                    rotate: mode === 'ytd' ? 0 : -45,
                    hideOverlappingLabels: false,
                    trim: false
                  }
                },
                yaxis: {
                  ...me.baseChartOptions.yaxis,
                  labels: {
                    ...me.baseChartOptions.yaxis.labels,
                    ...dollarFormat
                  }
                },
                tooltip: {
                  ...me.baseChartOptions.tooltip,
                  y: { ...dollarFormat }
                },
                chart: {
                  ...me.baseChartOptions.chart,
                  id: `affiliate-chart-${mode}-${categoryKey}`,
                  events: {}
                }
              }
            };
          });
      },
        // Column visibility by role (Configurations/Permissions):
        // admin sees everything, affiliate sees registrations + "*Amount" columns, everyone else sees no "*Amount" columns
        visibleHeaders() {
          const me = this;
          const headers = Array.isArray(me.headers) ? me.headers : [];
          if (me.isAdmin) return headers;
          const isAmount = h => /Amount$/.test(h.value);
          if (me.isAffiliate) {
            return headers.filter(h => h && (h.value === 'platform' || h.value === 'registered' || isAmount(h)));
          }
          return headers.filter(h => h && !isAmount(h));
        },
        filteredResults() {
          const me = this;
        
        let filtered = me.results;
      
      // Filter by selected email
        const hasUsableEmail = (filtered || []).some(item => item && item.email);
        if (me.selectedEmail && hasUsableEmail) {
          filtered = filtered.filter(item => item.email === me.selectedEmail);
        }

        if (me.selectedPlatforms && me.selectedPlatforms.length > 0) {
          filtered = filtered.filter(item => item && me.selectedPlatforms.includes(item.platform));
        }
        
        return filtered;
      }
    },
    mounted(){
      const me = this
      const authUser = systemConnection.getAuthUser && systemConnection.getAuthUser();
      if (authUser && authUser.email) me.loginEmail = authUser.email;
      const today = new Date()
      const currentYear = today.getFullYear();
      const monthName = new Array("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December");

      const ytdOption = { text: `Year to Date ${currentYear}`, value: `ytd-${currentYear}`, mode: 'ytd', year: currentYear };
      me.dates.push(ytdOption);

      today.setDate(1);
      for (var i=0; i<=11; i++) {
          const curMonthName = monthName[today.getMonth()];
          const month = ("0" + (today.getMonth() + 1)).slice(-2)
          var yyyy = today.getFullYear()

          me.dates.push({text:`${curMonthName} ${yyyy}`,value:yyyy+month,mode:'month'})
          today.setMonth(today.getMonth() - 1);
      }

      me.selectedDate = ytdOption;
      me.onDateChange(ytdOption);
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.affiliate-page {
  --page-bg: #f6f7fb;
  --surface: #ffffff;
  --border: rgba(15, 23, 42, 0.10);
  --text: #0b1220;
  --muted: rgba(15, 23, 42, 0.64);
  --muted-2: rgba(15, 23, 42, 0.50);
  --shadow: 0 18px 50px rgba(15, 23, 42, 0.10);
  --shadow-sm: 0 10px 26px rgba(15, 23, 42, 0.08);
  --radius: 14px;
  --radius-sm: 12px;
  --accent: #7c3aed;

  min-height: 100vh;
  background: var(--page-bg);
  padding: 22px 18px 40px;
}

.affiliate-shell {
  max-width: 90%;
  margin: 0 auto;
}

.affiliate-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.affiliate-brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  user-select: none;
}

.affiliate-mark {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.92);
  background-image: url("~@/assets/logo.png");
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.14);
  flex-shrink: 0;
}

.affiliate-brand__text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.affiliate-brand__name {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted-2);
}

.affiliate-brand__page {
  margin-top: 2px;
  font-size: 20px;
  font-weight: 750;
  letter-spacing: 0;
  color: var(--text);
}

.affiliate-user-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.welcome-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.logout-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

.affiliate-controls {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  padding: 14px 14px 8px;
}

.is-disabled {
  opacity: 0.65;
  pointer-events: none;
}

.selector-row {
  margin: 0;
}

.selector-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0 0 8px;
  color: var(--muted-2);
}

.affiliate-controls ::v-deep .v-text-field--outlined fieldset {
  border-color: var(--border) !important;
  border-radius: 999px !important;
}

.affiliate-controls ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
  background: #ffffff;
  border-radius: 999px !important;
}

.affiliate-controls ::v-deep .v-text-field--outlined.v-input--is-focused > .v-input__control > .v-input__slot {
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
}

.affiliate-controls ::v-deep .v-select__selections {
  padding-left: 6px;
}

.affiliate-controls ::v-deep .v-chip {
  height: 24px;
  border-radius: 999px;
  font-size: 12px;
}

.affiliate-charts {
  margin-top: 16px;
}

.chart-grid {
  margin: 0;
}

.chart-state-card {
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}

.chart-state-card--placeholder {
  color: var(--muted);
}

.chart-card {
  height: 100%;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  padding: 14px 14px 10px;
}

.chart-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.chart-card-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  color: rgba(15, 23, 42, 0.86);
}

.chart-card-close {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  opacity: 0.7;
}

.chart-card-close:hover {
  opacity: 1;
}

.chart-empty {
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  background: rgba(15, 23, 42, 0.02);
  border: 1px dashed rgba(15, 23, 42, 0.14);
  color: var(--muted);
}

.chart-card ::v-deep .apexcharts-text {
  fill: rgba(15, 23, 42, 0.55) !important;
}

.chart-card ::v-deep .apexcharts-gridline {
  stroke: rgba(15, 23, 42, 0.10) !important;
}

.chart-card ::v-deep .apexcharts-tooltip {
  border: 1px solid rgba(15, 23, 42, 0.10) !important;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.14) !important;
  border-radius: 12px !important;
}

.affiliate-table-section {
  margin-top: 16px;
}

.table-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.table-card-title {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(15, 23, 42, 0.72);
}

.affiliate-table ::v-deep {
  box-shadow: none !important;
}

.affiliate-table ::v-deep thead th {
  background: rgba(15, 23, 42, 0.02);
  color: rgba(15, 23, 42, 0.70);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.affiliate-table ::v-deep tbody tr:hover {
  background: rgba(124, 58, 237, 0.04) !important;
}

.table-loading {
  padding: 10px 14px 14px;
}

.placeholder-text {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

@media (max-width: 600px) {
  .affiliate-page {
    padding: 16px 12px 28px;
  }
  .affiliate-controls {
    padding: 12px 12px 6px;
  }
}
</style>
